import React, { useContext, useEffect, useState } from "react";
import SectionHeader from "../components/section/SectionHeader";
import { ALARM_TABLE_HEADERS, VIEW_ALARMS_FORM_FIELDS } from "../constants";
import AuthContext from "../store/authContext";
import {
  errorAlert,
  makeApiRequest,
  transformAlarmRows,
  transformAlarmsResponseRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import TableActionButton from "../ui/TableActionButton";
import { addActiveAlarmComment, getActiveAlarmByRef } from "../api";
import ViewAlarmRow from "../components/dataTable/ViewAlarmRow";
import useAppTitle from "../hooks/useAppTitle";
import useHideSidePanel from "../hooks/useHideSidePanel";
import { Lang, Page, Operation } from "../translations/types";
import { getPageTranslationText } from "../translations/util";

const translations = getPageTranslationText({
  lang: Lang.EN,
  page: Page.ACTIVE_ALARM,
  op: Operation.READ,
});

const ActiveAlarms = () => {
  const {
    token,
    scopes: { fm_alarms: fmAlarms },
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  useAppTitle("Active Alarms - IWF Config Admin Tool (ICAT) by AT&T");
  const { showSidePanel, handleToggleSidePanel } = useHideSidePanel();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [inventoryData, setInventoryData] = useState<any[]>([]);

  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    pageNum: number;
    pageSize: number;
  }>({
    pageSize: 20,
    pageNum: 1,
  });

  const fetchActiveAlarms = async () => {
    setLoading(true);
    try {
      const { pageNum, pageSize } = searchQuery;
      const queryParams = new URLSearchParams({
        page: `${pageNum}`,
        pageSize: `${pageSize}`,
      });
      const response = await makeApiRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Rows: Array<any>;
        NbrOfRows: number;
        NbrOfPages: number;
      }>({
        path: `/api/v1/app-config/fault-mgmt/alarms?${queryParams}`,
        token,
      });
      setNumberOfRows(response.NbrOfRows);
      setNumberOfPages(response.NbrOfPages);
      setInventoryData(response.Rows);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveAlarms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  return (
    <>
      <SectionHeader
        pageTitle={translations.header}
        pageDescription={translations.headerSubtext}
        sideComponent={null}
      />

      <>
        {showSidePanel && selectedConnectionId ? (
          <ViewAlarmRow
            panelName={translations.viewRow.panelName}
            org={selectedConnectionId}
            esRef={selectedConnectionId}
            setShowEditPanel={handleToggleSidePanel}
            formFields={VIEW_ALARMS_FORM_FIELDS}
            transformResponseRow={transformAlarmsResponseRows}
            loadDataByRef={getActiveAlarmByRef}
            addComment={fmAlarms.includes("u") ? addActiveAlarmComment : null}
          />
        ) : null}

        <DataTableWithControls
          loading={loading}
          showTableControls={false}
          filterColumns={[]}
          selectedColumn={null}
          setSelectedColumn={() => null}
          onSearch={() => fetchActiveAlarms()}
          showCreateButton={false}
          rows={transformAlarmRows(inventoryData).map(
            (row: { id: string }) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            })
          )}
          headers={ALARM_TABLE_HEADERS}
          pageNum={searchQuery.pageNum}
          setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
          totalItems={numberOfRows}
          totalPages={numberOfPages}
          pageSize={searchQuery.pageSize}
          setPageSize={(pageSize) =>
            setSearhQuery({ ...searchQuery, pageSize })
          }
        />
      </>
    </>
  );
};

export default ActiveAlarms;
